import type { GroupAssignedWorksheet } from '@mathflat/domain/StudentWorksheet'
import { addHours, isAfter } from 'date-fns'
import { computed, makeObservable, observable } from 'mobx'
import { EMPTY_NUMBER_ID } from 'src/constant/common'

import commonRepo from '@/repository/common.repo'
import { typeSafeAssign } from '@/utils/common'
import { stringUtils } from '@/utils/String'

import { DTO } from '../DTO'

type AdapterInterface = Omit<GroupAssignedWorksheet, 'assignDatetime'> & {
  id: GroupAssignedWorksheet['worksheet']['id']
  assignDatetime: Date
}

export class GroupStudentWorksheet extends DTO implements AdapterInterface {
  id = EMPTY_NUMBER_ID
  worksheet = {
    id: EMPTY_NUMBER_ID,
    type: 'CUSTOM',
    title: '',
    titleTag: '',
    tag: 'BASIC',
    autoScorable: false,
    problemCount: 0,
    level: 1,
    school: 'ELEMENTARY',
    grade: '1',
    chapter: '',
    revision: 'CURRICULUM_15',
  } as GroupAssignedWorksheet['worksheet']
  assignedStudentList: GroupAssignedWorksheet['assignedStudentList'] = []
  score: GroupAssignedWorksheet['score'] = null
  status = 'INCOMPLETE' as GroupAssignedWorksheet['status']
  assignDatetime = new Date()

  constructor(data?: AdapterInterface) {
    super()
    makeObservable(this, {
      id: observable,
      worksheet: observable,
      assignedStudentList: observable,
      score: observable,
      status: observable,
      assignDatetime: observable,
      assignedData: computed,
      canAssignHomework: computed,
      isHomeworkPartiallyAssigned: computed,
    })
    if (data) typeSafeAssign(this, data)
  }

  get isNewAssigned() {
    return isAfter(addHours(this.assignDatetime, 24), new Date())
  }

  get korWorksheetLevel() {
    switch (this.worksheet.level) {
      case 1:
        return '하'
      case 2:
        return '중하'
      case 3:
        return '중'
      case 4:
        return '상'
      case 5:
        return '최상'
    }
  }

  get korWorksheetTag() {
    switch (this.worksheet.tag) {
      case 'BASIC':
        return '기본'
      case 'PRACTICE':
        return '연습문제'
      case 'HOMEWORK':
        return '숙제'
      case 'DAILY_TEST':
        return '일일테스트'
      case 'WEEKLY_TEST':
        return '주간테스트'
      case 'CHAPTER_TEST':
        return '단원평가'
      case 'ENTRANCE_TEST':
        return commonRepo.isSchoolflat ? '진단평가' : '입학테스트'
      case 'SCHOOL_PREPARE':
        return commonRepo.isSchoolflat ? '교과서 쌍둥이' : '내신대비'
      case 'PRACTICE_TEST':
        return '모의고사'
      case 'CSA_TEST':
        return '수능대비'
    }
  }

  get assignedStudentNameLabel() {
    const [firstStudent] = this.assignedStudentList
    if (!firstStudent) return null
    return stringUtils.makeAssignCountText(
      firstStudent.studentName,
      this.assignedStudentList.length,
    )
  }

  get assignedData() {
    const assignedStudentsCount = this.assignedStudentList.length
    const assignedHomeworkCount = this.assignedStudentList.filter((item) =>
      Boolean(item.studentHomeworkId),
    ).length
    return {
      assignedStudentsCount,
      assignedHomeworkCount,
    }
  }

  // assignedStudentsCount > 0 && 모든 학생에게 출제되지 않았으면 true
  get canAssignHomework() {
    const { assignedStudentsCount, assignedHomeworkCount } = this.assignedData
    if (!assignedStudentsCount) {
      return false
    }
    return assignedStudentsCount > assignedHomeworkCount
  }

  // 1명 이상에게 숙제 출제되었지만 모든 학생에게 출제되지 않았으면 true
  get isHomeworkPartiallyAssigned() {
    const { assignedHomeworkCount } = this.assignedData
    return this.canAssignHomework && assignedHomeworkCount > 0
  }
}
